<template>
  <v-dialog :model-value="value" width="400px" @click:outside="closeModal">
    <BaseDialogCard :title="tkey('modalTitle')" @close="closeModal">
      <v-container fluid class="swap-parameters__container">
        <v-row>
          <v-col class="pa-0">
            <v-form ref="swapParametersForm" v-model="valid" autocomplete="off" @submit.prevent>
              <v-row class="mt-5 mb-3">
                <v-col class="pa-0">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.runDescription') }}
                  </span>
                  <v-text-field
                    v-model="runDescription"
                    :rules="runDescriptionRules"
                    grey
                    single-line
                    data-id-e2e="swapDescriptionInput"
                    class="swap-parameters__input"
                  />
                </v-col>
              </v-row>
              <v-row class="border-bottom pb-4 mb-3">
                <v-col cols="4" class="pa-0">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.maxNumberOfSwaps') }}
                  </span>
                  <AssortmentTextField
                    v-model="maxSwaps"
                    :rules="greaterThanZeroRules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    run-validations-on-creation
                    data-id-e2e="swapMaxNoOfSwapsInput"
                    variant="underlined"
                    bg-color="transparent"
                  />
                </v-col>
              </v-row>
              <v-row class="border-bottom pb-4 mb-4">
                <v-col cols="7" class="pa-0 pr-5">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.forecastPeriod') }}
                  </span>
                  <RtlsCalendarPicker
                    :value="weekStartDate"
                    :allowed-dates="allowedStartDates"
                    :display-date-format="getDateFormats.long"
                    data-id-e2e="swapForecasterStartDateSelect"
                    @input="updateWeekStartDate"
                  />
                </v-col>
                <v-col cols="5" class="pa-0">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.numberOfSlices') }}
                  </span>
                  <v-text-field
                    v-model.number="numberOfSlices"
                    :rules="greaterThanZeroRules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    data-id-e2e="swapNumberOfSlicesInput"
                  />
                </v-col>
              </v-row>
              <v-row class="border-bottom pb-4 mb-3">
                <v-col class="pa-0 d-flex align-center">
                  <span class="swap-parameters__label mb-0 pr-3">
                    {{ tkey('form.forecastSliceLength') }}
                  </span>
                  <AssortmentTextField
                    v-model="sliceLength"
                    :rules="greaterThanZeroRules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    run-validations-on-creation
                    data-id-e2e="swapForecastSliceLengthSelect"
                    variant="underlined"
                    bg-color="transparent"
                  />
                </v-col>
              </v-row>
              <v-row class="border-bottom mb-3">
                <v-col class="pa-0 d-flex align-center swap-parameters__forecast-end">
                  <span class="swap-parameters__label mb-0 pr-1">
                    {{ tkey('form.forecastEnd') }}:
                  </span>
                  <span>
                    {{ formatDate(forecastEndDate, getDateFormats.longWithTime) }}
                  </span>
                  <ErrorTriangle class="end-date-error-triangle" :errors="errors" />
                </v-col>
              </v-row>
              <v-row class="border-bottom pb-7 mb-3">
                <v-col class="pa-0 d-flex align-start">
                  <span class="swap-parameters__label mb-0 pr-3 mt-2">
                    {{ tkey('form.optimiseAccordingTo') }}
                  </span>
                  <v-select
                    :disabled="validatingOptimiseOptions"
                    :model-value="optimiseAccordingTo"
                    :items="sizeSelectItems"
                    item-title="text"
                    item-value="value"
                    :error-messages="optimiseAccordingToErrorMessage"
                    :placeholder="t('sizeTypes.noOption')"
                    :append-icon="sizeTypeIconMap[optimiseAccordingTo] || '$empty-cube'"
                    width="250px"
                    data-id-e2e="swapOptimiseAccordingToSelect"
                    @update:model-value="selectSetting"
                  />
                </v-col>
              </v-row>
              <v-row class="border-bottom pb-4 mb-4">
                <v-col cols="4" class="pa-0 pr-5">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.unitVolume') }}
                  </span>
                  <v-text-field
                    v-model.number="volume"
                    :rules="rules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    data-id-e2e="swapOptimiserVolumeInput"
                  />
                </v-col>
                <v-col cols="4" class="pa-0">
                  <span class="swap-parameters__label d-block">
                    {{ tkey('form.sales') }}
                  </span>
                  <v-text-field
                    v-model.number="sales"
                    :rules="rules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    data-id-e2e="swapOptimiserSalesInput"
                  />
                </v-col>
                <v-col cols="4" class="pa-0">
                  <div class="d-flex justify-space-between">
                    <span class="swap-parameters__label d-block">
                      {{ tkey('form.margin') }}
                    </span>
                    <AssortmentTooltip
                      v-if="isMarginWithoutFundingEnabled"
                      :title="t('general.warning')"
                      :tooltip-sections="marginWithoutFundingSections"
                      :width="500"
                    />
                  </div>
                  <v-text-field
                    v-model.number="margin"
                    :rules="rules"
                    grey
                    class="swap-parameters__input swap-parameters__input--short"
                    single-line
                    data-id-e2e="swapOptimiserMarginInput"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-col>
        </v-row>
      </v-container>
      <template #footer>
        <PageActions
          :has-data-errors="hasDataErrors"
          :has-data-changes="true"
          :show-discard="false"
          save-btn-text="optimiseSwaps"
          @save="runOptimiseSwaps"
        >
          <template #right-btns>
            <v-row class="actions-row justify-space-between flex-grow-1">
              <v-btn class="text-outline" variant="text" @click="closeModal">
                {{ t('actions.cancel') }}
              </v-btn>
            </v-row>
          </template>
        </PageActions>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { assign, every, filter, get, isNull, map, size, without } from 'lodash';

// enums
import SIZE_OPTIONS from '@enums/size-types';

// composables
import { useScenarioOptimiserTooltipOptions } from '@/composables/use-tooltip-options';
import { useInputValidations } from '@/composables/input-validations';
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';

// stores
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useSimpleSwapRunsStore } from '@/stores/simple-swap-runs';

const defaultValues = {
  runDescription: null,
  maxSwaps: null,
  weekStartDate: null,
  numberOfSlices: 26,
  sliceLength: 2,
  optimiseAccordingTo: null,
  volume: 1,
  sales: 6,
  margin: 3,
};
// List of falsy entries, except zero, to be used on form validations
const falsyValues = [undefined, null, false, NaN, ''];

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  emits: ['close'],

  setup() {
    const { t, tkey } = useInternationalization('swapParametersModal');

    const { calculateEndDate, calculateStartDate, formatDate, formatDateForSave, dayjs } =
      useDayjs();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { marginWithoutFundingSections } = useScenarioOptimiserTooltipOptions();

    const { isMarginWithoutFundingEnabled } = useFeature();

    return {
      t,
      tkey,
      // date utils
      calculateEndDate,
      calculateStartDate,
      formatDateForSave,
      dayjs,
      // input validations
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      // dayjs
      formatDate,
      // tooltip options
      marginWithoutFundingSections,
      // feature flags
      isMarginWithoutFundingEnabled,
    };
  },

  data() {
    return {
      ...defaultValues,
      valid: true,
      runDescriptionRules: [this.isNotEmpty, this.isUniqueRunDescription],
      rules: [this.isNotEmpty, this.isInteger],
      greaterThanZeroRules: [this.isNotEmpty, this.isInteger, v => this.isNumberGreaterThan(v, 0)],
      optimiseAccordingToErrorMessage: [],
      validatingOptimiseOptions: false,
      sizeTypeIconMap: {
        [SIZE_OPTIONS.linearSpace]: '$linear-space',
        [SIZE_OPTIONS.cubicSpace]: '$cubic-space',
        [SIZE_OPTIONS.horizontalSpace]: '$horizontal-space',
        [SIZE_OPTIONS.frontalSpace]: '$frontal-space',
        [SIZE_OPTIONS.productCount]: '$empty-cube',
      },
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useSimpleSwapRunsStore, ['getSimpleSwapRunDescriptions']),
    ...mapState(useContextStore, ['getDateFormats', 'getDefaultReverseFormat', 'getClientConfig']),
    ...mapState(useScenariosStore, ['selectedScenario']),

    allowedStartDates() {
      const { performanceStartDate, performanceEndDate, performanceExcludedDates } =
        this.selectedWorkpackage;
      const parsedExcludedDates = map(
        performanceExcludedDates || [],
        ped => this.dayjs.utc(ped).format('YYYY-MM-DD') // no need to use config because this is for local comparison only
      );
      return date => {
        const parsedDate = this.dayjs.utc(date);
        // requires moment 2.13.0, currently on 2.19.2 https://momentjscom.readthedocs.io/en/latest/moment/05-query/06-is-between/
        // [ is inclusive. Forecaster start date can be >= performanceStartDate.
        // ) is exclusive. Forecaster start date must be < performanceEndDate
        // shouldn't matter about exclusive portion because end dates should always be Sundays and this must be Monday.
        return (
          parsedDate.isoWeekday() === 1 &&
          parsedDate.isBetween(performanceStartDate, performanceEndDate, 'day', '[)') &&
          parsedExcludedDates.includes(date) === false
        );
      };
    },

    forecastEndDate() {
      return this.calculateEndDate({
        startDate: this.weekStartDate,
        weeks: this.numberOfForecastWeeks,
        dateFormat: this.getDefaultReverseFormat,
      });
    },

    numberOfForecastWeeks() {
      const sliceValue = this.sliceLength;
      return this.numberOfSlices * sliceValue;
    },

    isEndDateValid() {
      return (
        this.dayjs.utc(this.selectedWorkpackage.performanceEndDate) >=
        this.dayjs.utc(this.forecastEndDate)
      );
    },

    isStartDateValid() {
      return (
        this.dayjs.utc(this.weekStartDate) >=
        this.dayjs.utc(this.selectedWorkpackage.performanceStartDate)
      );
    },

    hasEmptyFields() {
      // Disables optimise swaps button before form changes have been made to
      // trigger form validation rules
      const swapParameters = {
        runDescription: this.runDescription,
        maxSwaps: this.maxSwaps,
        weekStartDate: this.weekStartDate,
        numberOfSlices: this.numberOfSlices,
        sliceLength: this.sliceLength,
        optimiseAccordingTo: this.optimiseAccordingTo,
        volume: this.volume,
        sales: this.sales,
        margin: this.margin,
      };
      const parameterValues = Object.values(swapParameters);
      return size(parameterValues) !== size(without(parameterValues, ...falsyValues));
    },

    errors() {
      return {
        [this.tkey('errorMessages.endDateOutOfBoundsError')]: this.isEndDateValid,
        [this.tkey('errorMessages.periodOutOfBoundsError')]: this.isStartDateValid,
      };
    },

    error() {
      return !every(this.errors);
    },

    hasDataErrors() {
      return !this.valid || this.error || this.hasEmptyFields;
    },

    sizeSelectItems() {
      return Object.keys(SIZE_OPTIONS).map(key => ({
        value: SIZE_OPTIONS[key],
        text: this.t(`sizeTypes.${key}`),
      }));
    },
  },

  watch: {
    value() {
      if (!this.value) return;
      // Reset parameters when modal is reopened
      this.resetParameters();
    },

    optimiseAccordingTo: {
      immediate: true,
      handler(v) {
        this.isOptimiseAccordingToValid(v);
      },
    },
  },

  created() {
    this.fetchSimpleSwapRuns();
  },

  methods: {
    ...mapActions(useSimpleSwapRunsStore, ['fetchSimpleSwapRuns', 'addSimpleSwapRun']),
    ...mapActions(useScenariosStore, ['hasValidProductAttributes']),

    resetParameters() {
      assign(this, defaultValues);
      this.setInitialForecastStartDate();
    },

    updateWeekStartDate(value) {
      this.weekStartDate = value;
    },

    isUniqueRunDescription(value) {
      if (!value) return true;

      const runDescription = value.toLowerCase().trim();
      const matchingDescriptions = filter(
        this.getSimpleSwapRunDescriptions,
        rd => rd.toLowerCase() === runDescription
      );
      if (size(matchingDescriptions) > 0) {
        return this.t('validationErrors.unique', [this.tkey('form.runDescription')]);
      }
      return true;
    },

    isNumberGreaterThan(value, minimum) {
      // Prevents validation error when form is reset
      if (isNull(value)) return true;
      return this.isGreaterThan(value, minimum);
    },

    setInitialForecastStartDate() {
      if (!this.selectedWorkpackage) return;

      this.weekStartDate = this.calculateStartDate({
        endDate: this.selectedWorkpackage.performanceEndDate,
        weeks: this.numberOfForecastWeeks,
        dateFormat: this.getDefaultReverseFormat,
      });

      let isWeekStartDateInvalid = true;

      // If weekStartDate is not valid we set weekStartDate as nearest valid date
      while (isWeekStartDateInvalid) {
        if (this.allowedStartDates(this.weekStartDate)) {
          isWeekStartDateInvalid = false;
        } else {
          this.weekStartDate = this.dayjs
            .utc(this.weekStartDate)
            .add(7, 'days')
            .format(this.getDefaultReverseFormat);
        }
      }

      if (!this.isEndDateValid) {
        this.numberOfSlices = Math.round(
          this.dayjs
            .utc(this.selectedWorkpackage.performanceEndDate)
            .diff(this.dayjs.utc(this.weekStartDate), 'week') / 2
        );
      }
    },

    async runOptimiseSwaps() {
      if (this.hasDataErrors) return false;

      const payload = {
        scenarioId: get(this.selectedScenario, '_id'),
        runDescription: this.runDescription,
        maxSwaps: parseInt(this.maxSwaps, 10),
        forecastParameters: {
          weekStartDate: this.formatDateForSave(this.weekStartDate, this.getDefaultReverseFormat),
          numberOfSlices: parseInt(this.numberOfSlices, 10),
          sliceLength: parseInt(this.sliceLength, 10),
        },
        optimiserSettings: {
          optimiseAccordingTo: this.optimiseAccordingTo,
          utilityWeights: {
            volume: parseInt(this.volume, 10),
            sales: parseInt(this.sales, 10),
            margin: parseInt(this.margin, 10),
          },
        },
      };

      await this.addSimpleSwapRun(payload);
      this.closeModal();
    },

    closeModal() {
      this.$emit('close');
    },

    async isOptimiseAccordingToValid(value) {
      if (!value) {
        this.optimiseAccordingToErrorMessage.push(this.t('validationErrors.required'));
        return;
      }

      if (value === SIZE_OPTIONS.productCount) return;
      const key = Object.keys(SIZE_OPTIONS).find(k => SIZE_OPTIONS[k] === value);
      this.validatingOptimiseOptions = true;
      const { data } = await this.hasValidProductAttributes({
        scenarioId: this.selectedScenario._id,
        optimiseAccordingTo: key,
      });
      this.validatingOptimiseOptions = false;
      if (!data) {
        this.optimiseAccordingToErrorMessage.push(this.t(`validationErrors.sizeType.${key}`));
      }
    },

    selectSetting(setting) {
      this.optimiseAccordingToErrorMessage = [];
      this.optimiseAccordingTo = setting;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.swap-parameters {
  &__container {
    border-bottom: 1px solid $assortment-divider-colour;
    font-size: 1.2rem;
    padding: 0;
  }

  &__label {
    color: $assortment-text-colour;
    font-weight: bold;
    margin-bottom: 8px;
    white-space: nowrap;
  }

  &__input {
    &--short {
      width: 117px;
    }
  }

  &__forecast-end {
    line-height: 2.4rem;
  }
}

.end-date-error-triangle {
  padding-left: 8px;
}

:deep(.dialog-card__footer) {
  border-top-width: 0px;
}

:deep(.rtls-calendar-picker .v-input__slot) {
  background-color: $assortment-control-secondary-bg-colour;
}
</style>
